import Swal from "sweetalert2";
import {
    createRazorpayOrder,
    verifyRazorpayPayment,
    savePaymentTransaction,
} from "../../services/receipt.service";

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

// Load the SDK once, safely, without hanging if script tag already exists
const loadScript = (src) =>
    new Promise((resolve) => {
        if (typeof window !== "undefined" && window.Razorpay) {
            resolve(true);
            return;
        }
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            existing.addEventListener("load", () => resolve(true), { once: true });
            existing.addEventListener("error", () => resolve(false), { once: true });
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

export const openRazorpayCheckout = async (
    amount,
    values,
    setLoading,
    onPaymentSuccess,
    description = "Pooja Receipt Payment"
) => {
    const rupees = Number(amount);
    if (!Number.isFinite(rupees) || rupees <= 0) {
        Swal.fire({
            icon: "warning",
            title: "Invalid Amount",
            text: "Receipt amount must be greater than zero for online payment.",
        });
        return;
    }

    setLoading(true);

    // 1. Industry Standard: Call Backend Orders API to create Razorpay Order
    let orderData = null;
    try {
        orderData = await createRazorpayOrder({
            amount: rupees,
            devoteeName: values?.devoteeName || "",
            mobile: values?.mobile || "",
            receiptNumber: description,
        });
    } catch (orderErr) {
        setLoading(false);
        const errMsg =
            orderErr?.response?.data?.message ||
            orderErr?.message ||
            "Unable to create payment order on server.";
        console.error("Order creation error:", orderErr);
        Swal.fire({
            icon: "error",
            title: "Order Creation Failed",
            text: errMsg,
        });
        return;
    }

    if (!orderData || !orderData.orderId || !orderData.keyId) {
        setLoading(false);
        Swal.fire({
            icon: "error",
            title: "Payment Gateway Error",
            text: "Invalid order response received from server. Please check backend Razorpay configuration.",
        });
        return;
    }

    const { keyId, orderId, amount: amountPaise, currency } = orderData;

    const loaded = await loadScript(CHECKOUT_SRC);
    if (!loaded || !window.Razorpay) {
        setLoading(false);
        Swal.fire({
            icon: "error",
            title: "Network Error",
            text: "Razorpay SDK failed to load. Please check your internet connection.",
        });
        return;
    }
    setLoading(false);

    const handledFailures = new Set();

    const options = {
        key: keyId,
        amount: amountPaise || Math.round(rupees * 100),
        currency: currency || "INR",
        order_id: orderId,
        name: "Sri Raghavendra Swamy Mutt",
        description,
        image: "/tms_icon.png",

        handler: async function (response) {
            setLoading(true);
            const paymentId = response?.razorpay_payment_id;
            const rzpOrderId = response?.razorpay_order_id || orderId;
            const signature = response?.razorpay_signature;

            // Pre-open PDF window immediately while user gesture is still active.
            // This prevents browser popup blockers from blocking window.open()
            // after async verify/save calls complete.
            const pdfWindow = window.open('', '_blank');

            if (!paymentId || !signature) {
                if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                setLoading(false);
                Swal.fire({
                    icon: "error",
                    title: "Payment Error",
                    text: "Invalid response from Razorpay. Payment verification cannot proceed.",
                });
                return;
            }

            try {
                // 2. Industry Standard: Verify payment signature and capture status on backend
                const verification = await verifyRazorpayPayment({
                    orderId: rzpOrderId,
                    paymentId: paymentId,
                    signature: signature,
                });

                if (!verification || !verification.verified) {
                    if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                    setLoading(false);
                    Swal.fire({
                        icon: "error",
                        title: "Payment Verification Failed",
                        text: "Payment signature could not be verified. Receipt will not be generated.",
                    });
                    return;
                }

                // Record the payment transaction
                const payload = {
                    razorpayId: paymentId,
                    status: verification?.status || "captured",
                    method: verification?.method || "online",
                    mobileNumber: values?.mobile,
                    name: values?.devoteeName,
                    amount: rupees,
                };

                try {
                    await savePaymentTransaction(payload);
                } catch (saveErr) {
                    console.error("Error saving payment transaction:", saveErr);
                }

                // 3. Complete receipt generation with verified payment details
                await onPaymentSuccess({
                    id: paymentId,
                    paymentId: paymentId,
                    orderId: rzpOrderId,
                    signature: signature,
                    status: verification.status,
                    method: verification.method,
                    bank: verification.bank,
                    wallet: verification.wallet,
                    vpa: verification.vpa,
                }, pdfWindow);

            } catch (error) {
                if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                console.error("Error verifying payment:", error);
                setLoading(false);
                const errMsg =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Payment verification encountered an unexpected error.";
                await Swal.fire({
                    icon: "error",
                    title: "Payment Verification Failed",
                    html: `Verification failed for payment <b>${paymentId}</b>: ${errMsg}<br/>Please contact the temple office. <b>Do not pay again.</b>`,
                });
            }
        },

        prefill: {
            name: values?.devoteeName || "",
            contact: values?.mobile || "",
            email: values?.email || "",
        },

        notes: {
            mobile: values?.mobile || "",
            devotee: values?.devoteeName || "",
            receiptType: description,
        },

        modal: {
            ondismiss: () => {
                setLoading(false);
            },
            escape: true,
            backdropclose: false,
        },

        theme: {
            color: "#cca833",
        },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", async (response) => {
        setLoading(false);
        const failedPaymentId = response?.error?.metadata?.payment_id || null;
        const key = failedPaymentId || `${response?.error?.code}-${response?.error?.reason}`;
        if (handledFailures.has(key)) return;
        handledFailures.add(key);

        const payload = {
            razorpayId: failedPaymentId,
            status: "failed",
            method: "N/A",
            mobileNumber: values?.mobile,
            name: values?.devoteeName,
            amount: rupees,
            errorCode: response?.error?.code || "PAYMENT_FAILED",
            errorDescription: response?.error?.description || "Payment failed or cancelled",
        };

        try {
            await savePaymentTransaction(payload);
        } catch (err) {
            console.error("Error saving failed transaction:", err);
        }

        console.error("Payment Failed:", response?.error);
        Swal.fire({
            icon: "error",
            title: "Payment Failed",
            text: response?.error?.description || "Payment could not be completed. Please try again.",
        });
    });

    rzp.open();
};
